import { motion } from "framer-motion";
import { useMemo } from "react";
import { appStore } from "store";
import { CountdownTimer } from "../../CountDownTimer/CountDownTimer";
import { AnimatedProgressCard } from "./AnimatedProgressCard";

import AGNTHubIcon from "../../../../../assets/svgComponents/PresaleBlock/AGNTHubIcon.svg";
import { InfoBlock } from "ui-kit/InfoBlock/InfoBlock";
import { Round } from "types";
import {
  FALLBACK_LISTING_TOKEN_PRICE_USD,
  FALLBACK_NEXT_TOKEN_PRICE_USD,
  formatPresaleCurrentUsdLabel,
  formatPresaleUsdLabel,
  toFiniteNumber,
} from "utils/presaleDisplay";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { gradientRowBgClass } from "screens/Dashboard/utils/classes";
import { computeSaleProgressMetrics } from "utils/saleProgress";

const customShadow = `shadow-[0px_6px_7.5px_0px_#0000000f] max-lg:shadow-[0px_6px_7.5px_0px_#0000000f]`;
const cardGradient = `before:content-[''] before:absolute before:inset-0 before:p-px max-lg:before:p-px before:rounded-[8px] max-lg:before:rounded-[6px] before:[background:linear-gradient(121deg,rgba(255,255,255,1)_0%,rgba(221,224,235,1)_100%)] before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)] before:[-webkit-mask-composite:xor] before:[mask-composite:exclude] before:z-[1] before:pointer-events-none`;

export const PresaleTop = ({
  targetDate,
  currentRound,
  iResponsible = false,
  hideUsdRaised = false,
}: {
  targetDate: string;
  currentRound: Round | null;
  iResponsible?: boolean;
  /** Omit presale progress (USD raised) — e.g. on dashboard */
  hideUsdRaised?: boolean;
}) => {
  const { t } = useTranslation();
  const { totalPool, sold } = appStore();
  const fadeInRight = {
    initial: { opacity: 0, x: "-20px" },
    animate: { opacity: 1, x: 0 },
    transition: { delay: 0.3, duration: 0.6 },
  };

  const fadeUp = {
    initial: { opacity: 0, y: "20px" },
    animate: { opacity: 1, y: 0 },
    transition: { delay: 0.4, duration: 0.6 },
  };

  const PRICES = [
    {
      label: t("Current Price"),
      display: formatPresaleCurrentUsdLabel(currentRound?.tokenPrice),
    },
    {
      label: t("Next Price"),
      display: formatPresaleUsdLabel(
        toFiniteNumber(currentRound?.nextTokenPrice) ??
          FALLBACK_NEXT_TOKEN_PRICE_USD,
      ),
    },
    {
      label: t("Listing Price"),
      display: formatPresaleUsdLabel(
        toFiniteNumber(currentRound?.listingTokenPrice) ??
          FALLBACK_LISTING_TOKEN_PRICE_USD,
      ),
    },
  ];

  const saleProgress = useMemo(() => {
    const tokenPrice = toFiniteNumber(currentRound?.tokenPrice) ?? 0;
    const roundSold = Number(currentRound?.sold ?? 0);
    const roundPool = Number(currentRound?.pool ?? 0);

    if (roundPool > 0 && tokenPrice > 0) {
      const raisedUsd = roundSold * tokenPrice;
      const targetUsd = roundPool * tokenPrice;
      return {
        raisedUsd,
        targetUsd,
        percent: Math.min(100, (raisedUsd / targetUsd) * 100),
      };
    }

    return computeSaleProgressMetrics(currentRound, sold, totalPool);
  }, [currentRound, sold, totalPool]);

  return (
    <div
      className={clsx(
        "flex flex-col gap-[15px] max-lg:w-full",
        iResponsible
          ? "max-lg:gap-[6px] max-lg:justify-between max-lg:flex-1"
          : "max-lg:gap-[15px]",
      )}
    >
      <div className="relative">
        <motion.div
          initial={{ opacity: 0, x: "-30px" }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className={clsx(
            "flex h-[47px] max-lg:h-[47px] px-[8px] max-lg:px-[8px] justify-between items-center gap-[3px] max-lg:gap-[3px] p-[3px] max-lg:p-[3px] w-full rounded-[16px] max-lg:rounded-[16px] before:!rounded-[16px] max-lg:before:!rounded-[16px]",
            gradientRowBgClass,
          )}
        >
          <div className="flex flex-row items-center gap-[8px] max-lg:gap-[8px]">
            <div className="flex bg-[#252525] rounded-full items-center justify-center w-[32px] h-[32px] max-lg:w-[31px] max-lg:h-[31px]">
              <img src={AGNTHubIcon} alt="" />
            </div>

            <motion.h1
              className="mt-[-1px] max-lg:mt-[-1px] font-luxora-medium
                font-medium text-[20px] max-lg:text-20m leading-[22px] max-lg:leading-[22px] text-white tracking-[0] whitespace-nowrap"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              $ATTS Presale
            </motion.h1>
          </div>

          <div className="relative rounded-[8px] max-lg:rounded-[6px]">
            <div
              className={`rounded-[8px] max-lg:rounded-[6px] ${customShadow} ${cardGradient}`}
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                className="font-luxora-regular bg-black  flex items-center justify-center rounded-[8px] max-lg:rounded-[6px]  py-[8px] px-[12px] max-lg:py-[8px] max-lg:px-[12px] font-normal text-[#34c545] text-[14px] max-lg:text-[14px] tracking-[0] leading-[110%] whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
                transition={{
                  duration: 1,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
              >
                {t("LIVE")}
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        {...fadeInRight}
        className="flex flex-row items-center justify-between max-lg:gap-[4px] gap-[5px]"
      >
        {PRICES.map((price) => {
          return (
            <InfoBlock
              text={price.display}
              label={price.label}
              key={price.label}
            />
          );
        })}
      </motion.div>

      <CountdownTimer targetDate={currentRound?.finishAt ?? targetDate} />

      {!hideUsdRaised && saleProgress.targetUsd > 0 && (
        <AnimatedProgressCard
          currentRaised={saleProgress.raisedUsd}
          totalTarget={saleProgress.targetUsd}
          progress={saleProgress.percent}
        />
      )}
    </div>
  );
};
